import type { PortalTargetMap } from './use-portal-target-map';

import { Text } from '@chakra-ui/react';
import { type ReactNode, Suspense, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { DocumentHeadingSubpagesPortal } from '../tools/document-heading/document-heading-subpages-portal';
import { DocumentFilesBlock } from '../tools/files/document-files-block';

export function useDocumentHeadingPortalNodes({
  targets,
  parentPath,
  childDocuments,
  editorPlaceholderMuted,
  isNewDocument,
  onCreateSubpage,
  onNavigateToDocument,
  onRequestRename,
}: {
  targets: PortalTargetMap;
  parentPath: string;
  childDocuments: Array<{ id: string; path: string; label?: string }>;
  editorPlaceholderMuted: boolean;
  isNewDocument: boolean;
  onCreateSubpage?: () => void;
  onNavigateToDocument?: (path: string) => void;
  onRequestRename?: () => void;
}): ReactNode {
  return useMemo(() => {
    return Array.from(targets.entries()).map(([id, el]) =>
      createPortal(
        <DocumentHeadingSubpagesPortal
          key={id}
          parentPath={parentPath}
          children={childDocuments}
          editorPlaceholderMuted={editorPlaceholderMuted}
          isNewDocument={isNewDocument}
          onCreateSubpage={onCreateSubpage}
          onNavigateToDocument={onNavigateToDocument}
          onRequestRename={onRequestRename}
        />,
        el,
      ),
    );
  }, [
    targets,
    parentPath,
    childDocuments,
    editorPlaceholderMuted,
    isNewDocument,
    onCreateSubpage,
    onNavigateToDocument,
    onRequestRename,
  ]);
}

export function useFilesPortalNodes({
  targets,
  documentId,
  apiHost,
  userEmail,
  parentPath,
}: {
  targets: PortalTargetMap;
  documentId?: string;
  apiHost: string;
  userEmail: string;
  parentPath: string;
}): ReactNode {
  return useMemo(() => {
    return Array.from(targets.entries()).map(([id, el]) =>
      createPortal(
        <Suspense
          key={id}
          fallback={
            <Text color="fg.muted" fontSize="sm">
              Loading uploads…
            </Text>
          }
        >
          <DocumentFilesBlock
            documentId={documentId}
            apiHost={apiHost}
            userEmail={userEmail}
            documentPath={parentPath}
          />
        </Suspense>,
        el,
      ),
    );
  }, [apiHost, documentId, targets, parentPath, userEmail]);
}
